import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate, useSearchParams } from 'react-router-dom'
import Pagination from '@mui/material/Pagination'
import TextField from '@mui/material/TextField'
import { Button } from '@mui/material'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import FilterListIcon from '@mui/icons-material/FilterList'
import RestartAltIcon from '@mui/icons-material/RestartAlt'
import HistoryIcon from '@mui/icons-material/History'
import VisibilityIcon from '@mui/icons-material/Visibility'
import Form from 'react-bootstrap/Form'
import Modal from 'react-bootstrap/Modal'
import ApiCaller from '../../constants/ApiCaller'
import urls from '../../constants/apiurl'
import { showDangerToast } from '../../constants/toaster'
import {
  updateTopHeader,
  uplodateLoading
} from '../../../redux/features/common/commonSlice'
import NoDataImg from '../../../assets/img/no-data.gif'
import './GradeUsersList.css'
import './app.css'

const DEFAULT_PAGE_SIZE = 20

const isApiSuccess = (response) =>
  response?.successful === true ||
  response?.status === 200 ||
  response?.status === '200'

const formatDateTime = (value) => {
  if (!value) return '--'
  try {
    return new Date(value).toLocaleString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    })
  } catch {
    return String(value)
  }
}

const emptyFilters = {
  userName: '',
  changeType: '',
  startDate: '',
  endDate: ''
}

export const GradeChangeLogList = () => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const userData = useSelector((state) => state?.userData)
  const LOADING_ENABLE = useSelector((state) => state?.loading)

  const adminHeaders = useMemo(() => {
    const userName =
      userData?.email || userData?.emailId || userData?.userName || ''
    return userName ? { userName } : {}
  }, [userData?.email, userData?.emailId, userData?.userName])

  const [filters, setFilters] = useState({
    ...emptyFilters,
    userName: searchParams.get('userName') || ''
  })
  const [appliedFilters, setAppliedFilters] = useState({
    ...emptyFilters,
    userName: searchParams.get('userName') || ''
  })
  const [filterUserId, setFilterUserId] = useState(searchParams.get('userId') || '')
  const [rows, setRows] = useState([])
  const [errorMessage, setErrorMessage] = useState('')
  const [page, setPage] = useState(1)
  const [pageInput, setPageInput] = useState(1)
  const [selectedLog, setSelectedLog] = useState(null)
  const [pagination, setPagination] = useState({
    page: 0,
    size: DEFAULT_PAGE_SIZE,
    totalPages: 0,
    totalItems: 0
  })

  useEffect(() => {
    dispatch(updateTopHeader('Grade Change Log'))
  }, [dispatch])

  const fetchLogs = useCallback(
    async (pageIndex = 0, filterValues = appliedFilters) => {
      try {
        dispatch(uplodateLoading(true))
        setErrorMessage('')

        const token = userData?.access_token
        if (!token) {
          showDangerToast('Access token missing. Please log in again.')
          return
        }

        const body = {
          userName: filterValues.userName?.trim() || null,
          userId: filterValues.userName?.trim() ? null : (filterUserId || null),
          changeType: filterValues.changeType || null,
          startDate: filterValues.startDate || null,
          endDate: filterValues.endDate || null,
          pageNumber: pageIndex,
          pageSize: DEFAULT_PAGE_SIZE
        }

        const res = await ApiCaller.post(
          body,
          urls.gradeChangeLogList,
          token,
          adminHeaders
        )
        const response = res?.response ? await res.response.json() : null

        if (!response) {
          setRows([])
          setErrorMessage('Failed to load grade change logs')
          return
        }

        if (isApiSuccess(response)) {
          const data = response.data || {}
          const items = data.items || data.content || []
          setRows(items)
          setPagination({
            page: data.pageNumber ?? pageIndex,
            size: data.pageSize ?? DEFAULT_PAGE_SIZE,
            totalPages: data.totalPages ?? 0,
            totalItems: data.totalItems ?? 0
          })
          if (items.length === 0 && response.message) {
            setErrorMessage(response.message)
          }
        } else {
          setRows([])
          setErrorMessage(response.message || 'Failed to load grade change logs')
        }
      } catch (error) {
        console.error('Grade change log fetch error:', error)
        setRows([])
        setErrorMessage(error?.message || 'Error fetching grade change logs')
        showDangerToast(error?.message || 'Error fetching grade change logs')
      } finally {
        dispatch(uplodateLoading(false))
      }
    },
    [adminHeaders, appliedFilters, dispatch, filterUserId, userData?.access_token]
  )

  useEffect(() => {
    fetchLogs(0, appliedFilters)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleApplyFilter = () => {
    setAppliedFilters({ ...filters })
    if (filters.userName?.trim()) {
      setFilterUserId('')
    }
    setPage(1)
    setPageInput(1)
    fetchLogs(0, filters)
  }

  const handleResetFilter = () => {
    setFilters({ ...emptyFilters })
    setAppliedFilters({ ...emptyFilters })
    setFilterUserId('')
    setPage(1)
    setPageInput(1)
    fetchLogs(0, emptyFilters)
  }

  const handlePageChange = (_e, value) => {
    setPage(value)
    setPageInput(value)
    fetchLogs(value - 1, appliedFilters)
  }

  const handlePageInputSubmit = () => {
    const value = Number(pageInput)
    const totalPages = pagination.totalPages || 1
    if (value >= 1 && value <= totalPages) {
      handlePageChange(null, value)
    } else {
      setPageInput(page)
    }
  }

  const openUser = (userId) => {
    if (userId) navigate(`/user-manager/${userId}`)
  }

  const showTable = rows.length > 0
  const showEmpty = !LOADING_ENABLE && rows.length === 0 && !errorMessage

  return (
    <div className='table-responsive'>
      <div className='grade-users-toolbar' style={{ padding: '0.85rem 1.25rem', margin: '0 1rem 0.75rem' }}>
        <div className='d-flex align-items-center justify-content-between flex-wrap gap-2'>
          <div className='d-flex align-items-center gap-2'>
            <HistoryIcon sx={{ fontSize: 26, opacity: 0.9 }} />
            <div>
              <h5 className='mb-0 text-white'>Grade Change Log</h5>
              <small style={{ opacity: 0.85, fontSize: '0.75rem' }}>
                Creator grade / grade-value change history
              </small>
            </div>
          </div>
          <Button
            variant='outlined'
            size='small'
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/grade-manager')}
            sx={{
              color: '#fff',
              borderColor: 'rgba(255,255,255,0.6)',
              '&:hover': { borderColor: '#fff', bgcolor: 'rgba(255,255,255,0.1)' }
            }}
          >
            Back
          </Button>
        </div>

        <div className='grade-users-stats' style={{ marginTop: '0.65rem', gap: '0.65rem' }}>
          <div className='grade-users-stat' style={{ padding: '0.35rem 0.75rem', minWidth: 90 }}>
            <div className='grade-users-stat-label'>Total</div>
            <div className='grade-users-stat-value' style={{ fontSize: '1rem' }}>
              {pagination.totalItems}
            </div>
          </div>
          <div className='grade-users-stat' style={{ padding: '0.35rem 0.75rem', minWidth: 90 }}>
            <div className='grade-users-stat-label'>Page</div>
            <div className='grade-users-stat-value' style={{ fontSize: '1rem' }}>
              {(pagination.page ?? 0) + 1}/{pagination.totalPages || 1}
            </div>
          </div>
        </div>
      </div>

      <div className='grade-users-filters' style={{ padding: '0.75rem 1rem' }}>
        <div className='grade-users-filters-toolbar'>
          <div
            className='grade-users-filters-grid'
            style={{
              gridTemplateColumns:
                'minmax(160px, 1.2fr) minmax(140px, 1fr) minmax(120px, 1fr) minmax(120px, 1fr) auto'
            }}
          >
            <div className='grade-users-field'>
              <Form.Label>Username</Form.Label>
              <Form.Control
                size='sm'
                type='text'
                placeholder='Search by username'
                value={filters.userName}
                onChange={(e) =>
                  setFilters((prev) => ({ ...prev, userName: e.target.value }))
                }
                onKeyDown={(e) => e.key === 'Enter' && handleApplyFilter()}
              />
            </div>

            <div className='grade-users-field'>
              <Form.Label>Change Type</Form.Label>
              <Form.Control
                size='sm'
                as='select'
                value={filters.changeType}
                onChange={(e) =>
                  setFilters((prev) => ({ ...prev, changeType: e.target.value }))
                }
              >
                <option value=''>All</option>
                <option value='USER_GRADE_CHANGE'>USER_GRADE_CHANGE</option>
                <option value='GRADE_VALUE_CHANGE'>GRADE_VALUE_CHANGE</option>
              </Form.Control>
            </div>

            <div className='grade-users-field'>
              <Form.Label>Start Date</Form.Label>
              <Form.Control
                size='sm'
                type='date'
                value={filters.startDate}
                onChange={(e) =>
                  setFilters((prev) => ({ ...prev, startDate: e.target.value }))
                }
              />
            </div>

            <div className='grade-users-field'>
              <Form.Label>End Date</Form.Label>
              <Form.Control
                size='sm'
                type='date'
                value={filters.endDate}
                onChange={(e) =>
                  setFilters((prev) => ({ ...prev, endDate: e.target.value }))
                }
              />
            </div>

            <div className='grade-users-filters-actions'>
              <Button
                variant='contained'
                color='primary'
                size='small'
                startIcon={<FilterListIcon />}
                onClick={handleApplyFilter}
              >
                Apply
              </Button>
              <Button
                variant='outlined'
                color='secondary'
                size='small'
                startIcon={<RestartAltIcon />}
                onClick={handleResetFilter}
              >
                Reset
              </Button>
            </div>
          </div>
        </div>
      </div>

      {errorMessage && (
        <div className='px-3 pb-2 pt-2'>
          <div className='alert alert-warning mb-0 py-2' role='alert'>
            {errorMessage}
          </div>
        </div>
      )}

      {showTable ? (
        <>
          <table className='table table-sm table-hover align-items-center table-flush table-striped mb-0'>
            <thead className='thead-dark text-white'>
              <tr className='text-white text-center'>
                <th className='text-white text-center'>S.No</th>
                <th className='text-white text-center'>Created At</th>
                <th className='text-white text-center'>Username</th>
                <th className='text-white text-center'>Type</th>
                <th className='text-white text-center'>Grade</th>
                <th className='text-white text-center'>Action</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row, idx) => (
                <tr key={row.uuid || row.id} className='text-center'>
                  <td>{pagination.page * pagination.size + idx + 1}</td>
                  <td style={{ whiteSpace: 'nowrap', fontSize: '0.85rem' }}>
                    {formatDateTime(row.createdAt)}
                  </td>
                  <td>
                    <button
                      type='button'
                      className='btn btn-link p-0 text-primary'
                      style={{ fontSize: '0.9rem' }}
                      title={row.userId}
                      onClick={() => openUser(row.userId)}
                    >
                      {row.userName || row.userId || '--'}
                    </button>
                  </td>
                  <td style={{ fontSize: '0.85rem' }}>
                    {row.changeType === 'GRADE_VALUE_CHANGE'
                      ? 'Value Change'
                      : row.changeType === 'USER_GRADE_CHANGE'
                        ? 'Grade Change'
                        : row.changeType || '--'}
                  </td>
                  <td style={{ fontSize: '0.85rem' }}>
                    {row.oldGradeName || row.oldGradeId || '--'} →{' '}
                    {row.newGradeName || row.newGradeId || '--'}
                  </td>
                  <td>
                    <Button
                      size='small'
                      variant='contained'
                      color='primary'
                      startIcon={<VisibilityIcon fontSize='small' />}
                      onClick={() => setSelectedLog(row)}
                    >
                      View
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          <div className='card-footer py-3 grade-users-footer'>
            <span className='grade-users-footer-info' style={{ fontSize: '0.85rem' }}>
              Page {pagination.page + 1} of {pagination.totalPages || 1} —{' '}
              {pagination.totalItems} total
            </span>
            <div className='grade-users-footer-controls'>
              <TextField
                label='Go to page'
                type='number'
                inputProps={{ min: 1, max: pagination.totalPages || 1 }}
                value={pageInput}
                onChange={(e) => setPageInput(e.target.value)}
                onBlur={handlePageInputSubmit}
                onKeyDown={(e) => e.key === 'Enter' && handlePageInputSubmit()}
                size='small'
                sx={{ width: 110 }}
              />
              <Pagination
                count={pagination.totalPages || 1}
                page={page}
                onChange={handlePageChange}
                color='secondary'
                size='small'
              />
            </div>
          </div>
        </>
      ) : showEmpty || errorMessage ? (
        <div className='text-center py-4'>
          <img width='20%' src={NoDataImg} alt='No Data' />
          <br />
          <label style={{ fontWeight: 'bold' }}>
            {LOADING_ENABLE
              ? 'Loading Data...'
              : errorMessage || 'No Data Found'}
          </label>
        </div>
      ) : (
        <div className='text-center py-4'>
          <label style={{ fontWeight: 'bold' }}>Loading Data...</label>
        </div>
      )}

      <Modal
        show={Boolean(selectedLog)}
        onHide={() => setSelectedLog(null)}
        centered
        size='md'
      >
        <Modal.Header closeButton>
          <Modal.Title style={{ fontSize: '1.1rem' }}>
            Grade Change Details
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedLog && (
            <table className='table table-sm table-bordered mb-0'>
              <tbody>
                <tr>
                  <th style={{ width: '40%' }}>Created At</th>
                  <td>{formatDateTime(selectedLog.createdAt)}</td>
                </tr>
                <tr>
                  <th>Username</th>
                  <td>
                    <button
                      type='button'
                      className='btn btn-link p-0'
                      onClick={() => {
                        const uid = selectedLog.userId
                        setSelectedLog(null)
                        openUser(uid)
                      }}
                    >
                      {selectedLog.userName || selectedLog.userId || '--'}
                    </button>
                  </td>
                </tr>
                <tr>
                  <th>User ID</th>
                  <td style={{ wordBreak: 'break-all' }}>
                    {selectedLog.userId || '--'}
                  </td>
                </tr>
                <tr>
                  <th>Change Type</th>
                  <td>{selectedLog.changeType || '--'}</td>
                </tr>
                <tr>
                  <th>Old → New Grade</th>
                  <td>
                    {selectedLog.oldGradeName || selectedLog.oldGradeId || '--'} →{' '}
                    {selectedLog.newGradeName || selectedLog.newGradeId || '--'}
                  </td>
                </tr>
                <tr>
                  <th>Old → New Value</th>
                  <td>
                    {selectedLog.oldGradeValue ?? '--'} →{' '}
                    {selectedLog.newGradeValue ?? '--'}
                  </td>
                </tr>
                <tr>
                  <th>Freeze Score</th>
                  <td>{selectedLog.freezeScore ?? 0}</td>
                </tr>
                <tr>
                  <th>Freeze Chips</th>
                  <td>{selectedLog.freezeChips ?? 0}</td>
                </tr>
                <tr>
                  <th>Transferred At Change</th>
                  <td>{selectedLog.transferredChipsAtChange ?? 0}</td>
                </tr>
                <tr>
                  <th>Total Chips At Change</th>
                  <td>{selectedLog.totalChipsAtChange ?? 0}</td>
                </tr>
                <tr>
                  <th>Changed By</th>
                  <td>{selectedLog.changedBy || '--'}</td>
                </tr>
              </tbody>
            </table>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant='contained'
            color='secondary'
            onClick={() => setSelectedLog(null)}
          >
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  )
}

export default GradeChangeLogList
